import { defaultProductFilterSetup, ProductFilter } from '@ph-model/filters/product-filter';
import { ProductFilters } from '@ph-model/filters/product-filters';
import { ProductItem } from '@ph-model/response';

import { SurchargeOption } from '@ph-model/response/interface';
import { SortingService } from '../services/sorting.service';

function fetchFiltersByProduct(products: ProductItem[] | undefined): ProductFilter[] {
  const productFilters = products?.reduce<ProductFilter[]>((filters: ProductFilter[], elem: ProductItem) => {
    const match = filters.find((item) => {
      return (
        item.name === (elem.productDescription.enUsProductDescription || elem.productDescription.frCaProductDescription)
      );
    });
    if (!match) {
      filters.push({
        ...defaultProductFilterSetup,
        name: elem.productDescription.enUsProductDescription || elem.productDescription.frCaProductDescription,
        groupName: elem.productCategory,
        property: 'product',
      });
    }

    return filters;
  }, []);

  return SortingService.sortStrings(productFilters || []);
}

function fetchFiltersByPlan(products: ProductItem[] | undefined): ProductFilter[] {
  const planFilters = products?.reduce<ProductFilter[]>((filters: ProductFilter[], elem: ProductItem) => {
    const match = filters.find((item) => {
      return item.name === (elem.planName.enUsPlanName || elem.planName.frCaPlanName);
    });
    if (!match) {
      filters.push({
        ...defaultProductFilterSetup,
        name: elem.planName.enUsPlanName || elem.planName.frCaPlanName,
        groupName: elem.productCategory,
        property: 'product',
      });
    }

    return filters;
  }, []);

  return SortingService.sortStrings(planFilters || []);
}

function fetchFiltersByCoverage(products: ProductItem[] | undefined): ProductFilter[] {
  const coverageFilters = products?.reduce<ProductFilter[]>((filters: ProductFilter[], elem: ProductItem) => {
    const match = filters.find((item) => {
      return item.name === elem.coverageDescription;
    });
    if (!match) {
      filters.push({
        ...defaultProductFilterSetup,
        name: elem.coverageDescription,
        groupName: elem.productCategory,
        property: 'product',
      });
    }

    return filters;
  }, []);

  return SortingService.sortStrings(coverageFilters || []);
}

function fetchFiltersByDeductible(products: ProductItem[] | undefined): ProductFilter[] {
  const deductibleFilters = products?.reduce((filters: ProductFilter[], elem: ProductItem) => {
    const index = filters.some((item) => {
      let deductibleName: string = elem.deductible.toString();
      if (elem.disappearingDeductible) {
        deductibleName = elem.deductible + '(D)';
      }

      return item.name === deductibleName;
    });
    if (!index) {
      let deductibleName: string = elem.deductible.toString();
      if (elem.disappearingDeductible) {
        deductibleName = elem.deductible + '(D)';
      }
      filters.push({ ...defaultProductFilterSetup, name: deductibleName, property: 'deductible' });
    }

    return filters;
  }, []);

  return (deductibleFilters || []).sort((d1, d2) => Number(d1.name) - Number(d2.name));
}

function fetchFiltersByStartingInterval(products: ProductItem[] | undefined): ProductFilter[] {
  const termFilters = products?.reduce((filters: ProductFilter[], elem: ProductItem) => {
    if (!elem.startingInterval) {
      return filters;
    }

    const index = filters.some((item) => {
      return item.name === elem.startingInterval;
    });
    if (!index) {
      filters.push({ ...defaultProductFilterSetup, name: elem.startingInterval, property: 'startingInterval' });
    }

    return filters;
  }, []);

  return (termFilters || []).sort((t1, t2) => Number(t1.name) - Number(t2.name));
}

function fetchFiltersByTerm(products: ProductItem[] | undefined): ProductFilter[] {
  const termFilters = products?.reduce((filters: ProductFilter[], elem: ProductItem) => {
    const index = filters.some((item) => {
      return item.name === elem.maxTerm;
    });
    if (!index) {
      filters.push({ ...defaultProductFilterSetup, name: elem.maxTerm, label: elem.maxTerm == '999' ? 'Unlimited' : undefined, property: 'term' });
    }

    return filters;
  }, []);

  return (termFilters || []).sort((t1, t2) => Number(t1.name) - Number(t2.name));
}

function fetchFiltersByMileage(products: ProductItem[] | undefined): ProductFilter[] {
  const mileageFilters = products?.reduce<ProductFilter[]>((filters, elem: ProductItem) => {
    const index = filters.some((item: ProductFilter) => {
      return item.name === elem.termMileage;
    });
    if (!index && elem.termMileage !== undefined && elem.termMileage !== null && elem.termMileage !== '') {
      filters.push({ name: elem.termMileage, label: elem.termMileage == '999999' ? 'Unlimited' : undefined, selected: false, display: true } as ProductFilter);
    }

    return filters;
  }, []);

  return (mileageFilters || []).sort((m1, m2) => Number(m1.name) - Number(m2.name));
}

function isProductSurchargesOfTypeTW(product: ProductItem): boolean {
  return ['TW', 'PROTECTION'].includes(product.productType);
}

function fetchFiltersByVSPSurcharge(products: ProductItem[] | undefined): ProductFilter[] {
  return (products || []).reduce<ProductFilter[]>((filters, product: ProductItem) => {
    if (isProductSurchargesOfTypeTW(product)) {
      return filters;
    }
    const surcharges = product.surchargeOptions.surchargeOption;
    if (surcharges) {
      surcharges.forEach((surcharge: SurchargeOption) => {
        const label =
          surcharge.label.enUsLabel || surcharge.label.enLabel || surcharge.label.frCaLabel || surcharge.label.frLabel;
        const found = filters.find((item: ProductFilter) => {
          return item.name === label;
        });
        if (!found) {
          filters.push({ name: label, selected: false, display: true } as ProductFilter);
        }
      });
    }

    return filters;
  }, []);
}

function fetchFiltersByTWSurcharge(products: ProductItem[] | undefined): ProductFilter[] {
  const filtersByTWSurcharge = products?.reduce<ProductFilter[]>((filters, product: ProductItem) => {
    if (!isProductSurchargesOfTypeTW(product)) {
      return filters;
    }
    const surcharges = product.surchargeOptions.surchargeOption;

    if (surcharges) {
      surcharges.forEach((surcharge) => {
        const label =
          surcharge.label.enUsLabel || surcharge.label.enLabel || surcharge.label.frCaLabel || surcharge.label.frLabel;
        const found = filters.find((item) => {
          return item.name === label;
        });
        if (!found) {
          filters.push({ name: label, selected: false, display: true } as ProductFilter);
        }
      });
    }

    return filters;
  }, []);

  if (filtersByTWSurcharge && filtersByTWSurcharge.length > 0) {
    filtersByTWSurcharge.push({ name: 'None', selected: true, display: true } as ProductFilter);
  }

  return filtersByTWSurcharge || [];
}

export function calculateFilters(products: ProductItem[]): ProductFilters {
  return {
    product: fetchFiltersByProduct(products),
    plan: fetchFiltersByPlan(products),
    coverage: fetchFiltersByCoverage(products),
    startingInterval: fetchFiltersByStartingInterval(products),
    term: fetchFiltersByTerm(products),
    deductible: fetchFiltersByDeductible(products),
    mileage: fetchFiltersByMileage(products),
    vspSurcharge: fetchFiltersByVSPSurcharge(products),
    twSurcharge: fetchFiltersByTWSurcharge(products),
  };
}

